// Medewerker-notities (dossiernotities) — Spec 35
//
// HR of VP-medewerkers kunnen vertrouwelijke notities bijhouden over een
// medewerker (gesprekken, afspraken, ziektecontact, etc.).

import { z } from 'zod'

// ─── Enums (matching Prisma enums as string unions) ──────────────────────────

export const MEDEWERKER_NOTITIE_CATEGORIEEN = [
  'ALGEMEEN',
  'VERZUIM',
  'GESPREK',
  'AFSPRAAK',
  'OVERIG',
] as const
export type MedewerkerNotitieCategorie = (typeof MEDEWERKER_NOTITIE_CATEGORIEEN)[number]

export const MEDEWERKER_NOTITIE_CATEGORIE_LABELS: Record<MedewerkerNotitieCategorie, string> = {
  ALGEMEEN: 'Algemeen',
  VERZUIM:  'Verzuim',
  GESPREK:  'Gesprek',
  AFSPRAAK: 'Afspraak',
  OVERIG:   'Overig',
}

export const MEDEWERKER_NOTITIE_CATEGORIE_BADGE_VARIANTS: Record<
  MedewerkerNotitieCategorie,
  'default' | 'warning' | 'info' | 'success' | 'secondary'
> = {
  ALGEMEEN: 'default',
  VERZUIM:  'warning',
  GESPREK:  'info',
  AFSPRAAK: 'success',
  OVERIG:   'secondary',
}

// ─── Zod-schemas ─────────────────────────────────────────────────────────────

export const createMedewerkerNotitieSchema = z.object({
  // ISO yyyy-MM-dd datumstring van het contact of gesprek; kan in het verleden liggen.
  datum:     z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Ongeldig datum-formaat (YYYY-MM-DD)'),
  categorie: z.enum(MEDEWERKER_NOTITIE_CATEGORIEEN).default('ALGEMEEN'),
  inhoud:    z.string().min(1).max(4000),
  // batch-68 gespreksvoorbereiding (FO-346 §3.9) — "Vertrouwelijk (alleen HR)".
  // Alleen met HR_VERTROUWELIJK.canEdit; de API herkeurt server-side.
  isVertrouwelijk: z.boolean().optional(),
})
export type CreateMedewerkerNotitieInput = z.infer<typeof createMedewerkerNotitieSchema>

export const updateMedewerkerNotitieSchema = z.object({
  datum:     z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Ongeldig datum-formaat (YYYY-MM-DD)').optional(),
  categorie: z.enum(MEDEWERKER_NOTITIE_CATEGORIEEN).optional(),
  inhoud:    z.string().min(1).max(4000).optional(),
  // batch-68 gespreksvoorbereiding — de vlag wijzigen vraagt HR_VERTROUWELIJK.canEdit.
  isVertrouwelijk: z.boolean().optional(),
})
export type UpdateMedewerkerNotitieInput = z.infer<typeof updateMedewerkerNotitieSchema>

// ─── DTO voor API-response ────────────────────────────────────────────────────

export interface MedewerkerNotitieDTO {
  id:          string
  employeeId:  string
  datum:       string         // ISO yyyy-MM-dd
  categorie:   MedewerkerNotitieCategorie
  inhoud:      string
  auteurId:    string
  auteurNaam:  string | null
  createdAt:   string
  updatedAt:   string
  // batch-68 gespreksvoorbereiding (FO-346 §3.9) — vertrouwelijke HR-notitie.
  // Alleen zichtbaar voor de auteur en voor HR_VERTROUWELIJK.canView.
  isVertrouwelijk: boolean
}
